import de from "../i18n/de/freigabe.json";
import en from "../i18n/en/freigabe.json";

const CODES = new Set(["PACKAGE_RETIRED", "PACKAGE_GENERATION_CHANGED", "PACKAGE_RETIREMENT_PENDING", "PACKAGE_RETIREMENT_BUSY"]);

// Pakettransport (Welle 6): Codes aus Vorprüfung, Snapshot, Lauf und Änderungsbericht.
export const PACKAGE_TRANSPORT_CODES = new Set([
  "PACKAGE_TRANSPORT_SETUP_REQUIRED", "PACKAGE_TRANSPORT_SETUP_UNVERIFIED", "PACKAGE_TRANSPORT_APP_REQUIRED", "PACKAGE_TRANSPORT_APP_UNREACHABLE",
  "PACKAGE_TRANSPORT_DISABLED", "PACKAGE_TRANSPORT_UNREADABLE", "PACKAGE_TRANSPORT_ID_INVALID", "PACKAGE_TRANSPORT_DRAFT",
  "PACKAGE_TRANSPORT_DEPLOYMENT_UNKNOWN", "PACKAGE_EXPORT_UNCLEAR", "PACKAGE_TRANSPORT_SWITCHED_TO_ARTIFACT",
  "PACKAGE_DELETED_UPLOAD_FAILED", "PACKAGE_TRANSPORT_DELETE_REIMPORT", "PACKAGE_TRANSPORT_EDGE_UNVERIFIED",
  "PACKAGE_TRANSPORT_BASE_DIVERGED", "PACKAGE_TRANSPORT_PATCH_UNSAFE", "PACKAGE_TRANSPORT_TARGET_REPLACED",
  "PACKAGE_TRANSPORT_EXPORT_UNAVAILABLE", "PACKAGE_EXPORT_LAYOUT_UNREADABLE", "PACKAGE_TRANSPORT_LAYOUT_CONFLICT", "PACKAGE_EXPORT_MISMATCH",
  "PACKAGE_EXPORT_FILE_TOO_LARGE", "PACKAGE_EXPORT_TOO_LARGE", "PACKAGE_EXPORT_LARGE", "PACKAGE_EXPORT_RENAME_INCOMPLETE",
  "PACKAGE_EXPORT_RENAME_AMBIGUOUS", "PACKAGE_EXPORT_RENAME_UNSUPPORTED", "PACKAGE_EXPORT_PLANT_ID_UNCHANGED", "PLANT_TARGET_COLLISION",
  "GIT_READ_FAILED", "PACKAGE_EXPORT_UNAVAILABLE", "PACKAGE_TRANSPORT_MODE_CHANGED",
  "PACKAGE_TRANSPORT_TARGET_UNVERIFIED", "PACKAGE_TRANSPORT_TARGET_UNREADABLE",
  "REFERENCE_OWNERSHIP_UNKNOWN", "REFERENCE_SKIPPED_UNLOADABLE",
]);
// Der Server nennt hier Einzelheiten (iFlow-Namen, Gründe, Dateien); sein Text geht dem allgemeinen vor.
const SERVER_TEXT = new Set(["PACKAGE_TRANSPORT_DRAFT", "PACKAGE_TRANSPORT_DEPLOYMENT_UNKNOWN", "PACKAGE_EXPORT_UNCLEAR", "PACKAGE_TRANSPORT_BASE_DIVERGED",
  // Nennen Ordner, Datei, Objekt oder Werk.
  "PACKAGE_EXPORT_LAYOUT_UNREADABLE", "PACKAGE_EXPORT_FILE_TOO_LARGE", "PACKAGE_EXPORT_RENAME_INCOMPLETE", "PACKAGE_EXPORT_RENAME_AMBIGUOUS",
  "PACKAGE_EXPORT_RENAME_UNSUPPORTED", "PACKAGE_EXPORT_PLANT_ID_UNCHANGED",
  // DEV182: nennen Ziel, Typ bzw. betroffene IDs; ein unklarer Ausgang ist es nicht, es wurde nichts gestartet.
  "PACKAGE_TRANSPORT_TARGET_UNVERIFIED", "PACKAGE_TRANSPORT_TARGET_UNREADABLE",
  // DEV183: nennen die Referenz.
  "REFERENCE_OWNERSHIP_UNKNOWN", "REFERENCE_SKIPPED_UNLOADABLE"]);

const catalogOf = language => (String(language || "de").toLowerCase().startsWith("en") ? en : de);
const currentLanguage = () => (typeof window !== "undefined" ? window.__iioLang || "de" : "de");

/** Text eines Pakettransportschlüssels mit {{platzhalter}}; null, wenn der Schlüssel fehlt. */
export function packageTransportText(key, language = currentLanguage(), vars = {}) {
  const text = catalogOf(language).packageTransport?.[key];
  return typeof text === "string" ? text.replace(/\{\{(\w+)\}\}/g, (_, name) => (vars[name] ?? "")) : null;
}

/** Entwurfssperre beim Pakettransport, Einzahl oder Mehrzahl, mit den Namen der iFlows (gleicher Text wie der Server). */
export function packageDraftText(names, language = currentLanguage()) {
  const list = (names || []).filter(Boolean);
  return packageTransportText(list.length === 1 ? "PACKAGE_TRANSPORT_DRAFT_one" : "PACKAGE_TRANSPORT_DRAFT_other", language, { names: list.join(", ") });
}

/** Hinweis vor dem Start: Der Server lehnt Entwürfe im ganzen Paket voraussichtlich ab. */
export function packageDraftHint(names, language = currentLanguage()) {
  const list = (names || []).filter(Boolean);
  return packageTransportText(list.length === 1 ? "PACKAGE_TRANSPORT_DRAFT_HINT_one" : "PACKAGE_TRANSPORT_DRAFT_HINT_other", language, { names: list.join(", ") });
}

/** „Wird im Ziel entfernt: Name (Typ)“, der Typ nur, wenn er vorhanden ist. Gleiche Schlüssel in Scene3, Scene5 und Bericht. */
export function removedInTargetText(item, language = currentLanguage()) {
  const name = item?.name || item?.id || "";
  return typeof item?.type === "string" && item.type.trim()
    ? packageTransportText("PACKAGE_TRANSPORT_TARGET_REMOVED", language, { name, type: item.type })
    : packageTransportText("PACKAGE_TRANSPORT_TARGET_REMOVED_NAME", language, { name });
}

/**
 * Hinweis „Artefaktweg mit Hinweis“: Text eines Rückfalls (Precheck oder Snapshot), bei dem das Paket je iFlow weiterläuft.
 * Gibt es eine eigene Rückfallfassung (_FALLBACK), gilt sie; sonst der Katalogtext des Codes.
 */
export function packageFallbackText(code, language = currentLanguage()) {
  return packageTransportText(`${code}_FALLBACK`, language) || packageTransportText(code, language);
}

function packageTransportError(error, code, language) {
  // REFERENCE_*: der Katalogtext nennt die Referenz (referenceId des Hinweises).
  if (String(code).startsWith("REFERENCE_") && !(typeof (error?.body?.error ?? error?.message) === "string" && (error?.body?.error ?? error?.message).trim()))
  {
    // Ohne referenceId gilt die Fassung ohne Platzhalter (_NOID), sonst bliebe eine Lücke im Text.
    const id = error?.referenceId || error?.body?.referenceId || "";
    return (id ? null : packageTransportText(`${code}_NOID`, language)) || packageTransportText(code, language, { id });
  }
  const server = error?.body?.error ?? (typeof error?.error === "string" ? error.error : null);
  if (SERVER_TEXT.has(code) && typeof server === "string" && server.trim()) return server.trim();
  if (code === "PACKAGE_TRANSPORT_DRAFT") return packageDraftText(error?.body?.drafts || error?.drafts || [], language);
  return packageTransportText(code, language);
}

export function transportPackageError(error, language = currentLanguage()) {
  const code = typeof error === "string" ? error : error?.code || error?.body?.code;
  if (PACKAGE_TRANSPORT_CODES.has(code)) return packageTransportError(typeof error === "string" ? null : error, code, language);
  if (!CODES.has(code)) return null;
  return catalogOf(language).preflight.errors[code] || null;
}
